import * as React from 'react';
import {
  forEach,
  maxBy,
  minBy,
  slice,
  orderBy,
  capitalize,
  round,
} from 'lodash';
import moment from 'moment';

export const totalPriceOptions = [
  {
    id: 'all',
    name: 'All',
  },
  {
    id: '0k-50k',
    name: '0K - 50K',
    range: [0, 50000],
  },
  {
    id: '50k-100k',
    name: '50K - 100K',
    range: [50000, 100000],
  },
  {
    id: '100k-200k',
    name: '100K - 200K',
    range: [100000, 200000],
  },
  {
    id: '200k-400k',
    name: '200K - 400K',
    range: [200000, 400000],
  },
  {
    id: '400k-600k',
    name: '400K - 600K',
    range: [400000, 600000],
  },
  {
    id: '600k-800k',
    name: '600K - 800K',
    range: [600000, 800000],
  },
  {
    id: '800k-1m',
    name: '800K - 1M',
    range: [800000, 1000000],
  },
  {
    id: 'gt1m',
    name: '1M+',
    range: [1000000, null],
  },
];

export const mileageOptions = [
  {
    id: 'all',
    name: 'All',
  },
  {
    id: '0k-25k',
    name: '0K - 25K',
    range: [0, 25000],
  },
  {
    id: '25k-50k',
    name: '25K - 50K',
    range: [25000, 50000],
  },
  {
    id: '50k-75k',
    name: '50K - 75K',
    range: [50000, 75000],
  },
  {
    id: '75k-100k',
    name: '75K - 100K',
    range: [75000, 100000],
  },
  {
    id: 'gt100k',
    name: '100K+',
    range: [100000, null],
  },
];

export const pricePerMonthOptions = [
  {
    id: 'all',
    name: 'All',
  },
  {
    id: '0k-1k',
    name: '0K - 1K',
    range: [0, 1000],
  },
  {
    id: '1k-2k',
    name: '1K - 2K',
    range: [1000, 2000],
  },
  {
    id: '2k-3k',
    name: '2K - 3K',
    range: [2000, 3000],
  },
  {
    id: '3k-4k',
    name: '3K - 4K',
    range: [3000, 4000],
  },
  {
    id: 'gt4k',
    name: '4K+',
    range: [4000, null],
  },
];

export const allFuelConsumptionOptions = [
  {
    id: 'lt10',
    name: '<10 km/L',
    range: [null, 10],
  },
  {
    id: 'f10t14',
    name: '10 - 14.9 km/L',
    range: [10, 14.9],
  },
  {
    id: 'f15t19',
    name: '15 - 19.9 km/L',
    range: [15, 19.9],
  },
  {
    id: 'f20t24',
    name: '20 - 24.9 km/L',
    range: [20, 24.9],
  },
  {
    id: 'f25t29',
    name: '25 - 29.9 km/L',
    range: [25, 29.9],
  },
  {
    id: 'gt30',
    name: '>30 km/L',
    range: [30, null],
  },
];

export const allEngineCapacityOptions = [
  {
    id: 'lt661',
    name: '<661cc',
    range: [null, 661],
  },
  {
    id: '661-1600',
    name: '661cc - 1,600cc',
    range: [661, 1600],
  },
  {
    id: '1601-2000',
    name: '1,601cc - 2,000cc',
    range: [1601, 2000],
  },
  {
    id: '2001-3000',
    name: '2,001cc - 3,000cc',
    range: [2001, 3000],
  },
  {
    id: 'gt3000',
    name: '>3,000cc',
    range: [3000, null],
  },
];

export const allDealerTypeOptions = [
  {
    id: 'authorised_dealer',
    name: 'Authorised Dealer',
  },
  {
    id: 'parallel_importer',
    name: 'Parallel Importer',
  },
];

export const allVesOptions = [
  { name: 'A1 -$20,000', id: 'a1-20k' },
  { name: 'A2 -$10,000', id: 'a2-10k' },
  { name: 'A3 (Neutral)', id: 'a3-neutral' },
  { name: 'C1 +$10,000', id: 'c110k' },
  { name: 'C2 +$20,000', id: 'c220k' },
];

export const getNewCarsFilterOptions = (variants) => {
  const allOption = {
    id: 'all',
    name: 'All',
    count: 0,
  };
  const makeOptions = {
    all: allOption,
  };
  const bodyTypeOptions = {
    all: allOption,
  };
  const fuelTypeOptions = {
    all: allOption,
  };
  const fuelConsumptionOptions = {
    all: allOption,
  };
  const engineCapacities = {
    all: allOption,
  };
  const vesOptions = {
    all: allOption,
  };
  const dealerTypes = {
    all: allOption,
  };

  variants.forEach((variant) => {
    allOption.count++;

    const make = variant.model?.make;
    if (make) {
      let name = make.name;
      let id = make._id;
      if (!name) {
        name = 'Others';
      }
      if (!makeOptions[id]) {
        makeOptions[id] = {
          count: 1,
          name: name,
          id: id,
        };
      } else {
        makeOptions[id].count++;
      }
    }

    const bodyType = variant.carOverview.bodyType;
    if (!bodyTypeOptions[bodyType]) {
      bodyTypeOptions[bodyType] = {
        count: 1,
        name: capitalize(bodyType) || 'Others',
        id: bodyType,
      };
    } else {
      bodyTypeOptions[bodyType].count++;
    }

    const fuelType = variant.carOverview.fuelType;
    if (!fuelTypeOptions[fuelType]) {
      fuelTypeOptions[fuelType] = {
        count: 1,
        name: fuelType || 'Others',
        id: fuelType,
      };
    } else {
      fuelTypeOptions[fuelType].count++;
    }

    updateFilterOptions(
      variant.carOverview.fuelConsumption,
      allFuelConsumptionOptions,
      fuelConsumptionOptions,
      (value, option) => isNumberInRange(value, option.range)
    );
    updateFilterOptions(
      variant.carSpecifications.engineCapacity,
      allEngineCapacityOptions,
      engineCapacities,
      (value, option) => isNumberInRange(value, option.range)
    );
    updateFilterOptions(variant.carOverview.ves, allVesOptions, vesOptions);
    updateFilterOptions(
      variant.model?.dealerType,
      allDealerTypeOptions,
      dealerTypes
    );
  });

  return {
    makeOptions: orderBy(Object.values(makeOptions), ['count'], ['desc']),
    bodyTypeOptions: Object.values(bodyTypeOptions),
    fuelTypeOptions: Object.values(fuelTypeOptions),
    fuelConsumptionOptions: Object.values(fuelConsumptionOptions),
    engineCapacityOptions: Object.values(engineCapacities),
    dealerTypeOptions: Object.values(dealerTypes),
    vesOptions: Object.values(vesOptions),
  };
};

export const getUsedCarsFilterOptions = (usedCars) => {
  const allOption = {
    id: 'all',
    name: 'All',
    count: 0,
  };
  const makeOptions = {
    all: allOption,
  };
  const bodyTypeOptions = {
    all: allOption,
  };
  const fuelTypeOptions = {
    all: allOption,
  };

  usedCars.forEach((usedCar) => {
    allOption.count++;

    const make = usedCar.make;
    if (make) {
      if (!makeOptions[make]) {
        makeOptions[make] = {
          count: 1,
          name: make,
          id: make,
        };
      } else {
        makeOptions[make].count++;
      }
    }

    const bodyType = usedCar.vehicle_type;
    if (!bodyTypeOptions[bodyType]) {
      bodyTypeOptions[bodyType] = {
        count: 1,
        name: bodyType,
        id: bodyType,
      };
    } else {
      bodyTypeOptions[bodyType].count++;
    }

    const fuelType = usedCar.fuel_type;
    if (!fuelTypeOptions[fuelType]) {
      fuelTypeOptions[fuelType] = {
        count: 1,
        name: fuelType,
        id: fuelType,
      };
    } else {
      fuelTypeOptions[fuelType].count++;
    }
  });

  return {
    makeOptions: orderBy(Object.values(makeOptions), ['count'], ['desc']),
    bodyTypeOptions: Object.values(bodyTypeOptions),
    fuelTypeOptions: Object.values(fuelTypeOptions),
  };
};

const updateFilterOptions = (value, allOptions, options, compareFunc) => {
  const op = allOptions.find((option) =>
    compareFunc ? compareFunc(value, option) : option.id === value
  );
  if (op) {
    if (!options[op.id]) {
      options[op.id] = {
        count: 1,
        name: op.name,
        id: op.id,
      };
    } else {
      options[op.id].count++;
    }
  }
};

export const formatMoney = (val, postFix) => {
  if (!val) {
    return 'N/A';
  }
  const numVal = round(Number(val), 2);

  return `$${numVal.toLocaleString()}${postFix || ''}`;
};

export const formatNumber = (val, postFix, defaultValue) => {
  if (!val) {
    return defaultValue || 'N/A';
  }
  return `${Number(val).toLocaleString()}${postFix || ''}`;
};

export const isNumberInRange = (val, range) => {
  if (range[0] === null) {
    return val < range[1];
  }
  if (range[1] === null) {
    return val > range[0];
  }
  return range[0] <= val && val <= range[1];
};

export const HELP_CONTENT = {
  OMV: {
    title: 'OMV',
    content: (
      <div>
        <p>
          The Open Market Value (OMV) is the ‘actual’ price of the vehicle as
          assessed by Singapore Customs when sold for export to Singapore. This
          price includes purchase price, freight, insurance and all other
          charges incidental to the sale and delivery of the car to Singapore.
        </p>
        <p>
          If a car is de-registered within its first 10 years of its COE tenure,
          the owner is entitled to a Preferential Additional Registration Fee
          (PARF) rebate which is a percentage of the OMV. For a detailed
          calculation of the PARF rebate, please see LTA’s website{' '}
          <a href="https://onemotoring.lta.gov.sg/content/onemotoring/home/buying/rebates/parf-coe-rebate.html">
            here
          </a>
          .
        </p>
      </div>
    ),
  },
  COE: {
    title: 'COE',
    content: (
      <div>
        <p>
          The indicated Certificate of Entitlement (COE) value is the value of
          the COE secured when the car was first registered.{' '}
        </p>
        <p>
          COE is required for every car in Singapore to be registered for local
          use, and is a function of market demand and supply. There is a limited
          supply of COEs released and they have to be bidded for.
        </p>
        <p>
          If the vehicle is de-registered and yet still has remaining COE left,
          a rebate will be granted to the owner in the form of a pro-rated Quota
          Premium (QP) or remaining COE value.{' '}
        </p>
      </div>
    ),
  },
  ARF: {
    title: 'ARF',
    content: (
      <div>
        <p>
          The Additional Registration Fee (ARF) is a tax levied on a vehicle
          during registration. It is based on a percentage of the Open Market
          Value (OMV) of a vehicle as determined by the{' '}
          <a href="https://onemotoring.lta.gov.sg/content/onemotoring/home/buying/upfront-vehicle-costs/additional-registration-fee--arf-.html">
            LTA
          </a>
          . The ARF determines the PARF rebate, which is a key contributor to
          the de-registration value of a vehicle.{' '}
        </p>
      </div>
    ),
  },
};

export const getPageData = (items, page, pageSize) => {
  const offset = (page - 1) * pageSize;

  const currentPageItems = slice(items, offset, offset + pageSize);

  return {
    items: currentPageItems,
    from: (page - 1) * pageSize + 1,
    to: (page - 1) * pageSize + currentPageItems.length,
  };
};

export const groupPricesByDealers = (variantPrices) => {
  const result = [];
  const map = {};
  variantPrices.forEach((variantPrice) => {
    let cur = map[variantPrice.dealer.id];
    if (!cur) {
      cur = {
        dealer: variantPrice.dealer,
        prices: [variantPrice],
      };
      map[variantPrice.dealer.id] = cur;
      result.push(cur);
    } else {
      cur.prices.push(variantPrice);
    }
  });
  return result;
};

export const checkFilterRangeValue = (
  variantValue,
  filterValue,
  allOptions
) => {
  let isInRange = false;
  let filterValueAsArray = Array.from(filterValue);
  for (let i = 0; i < filterValueAsArray.length; i++) {
    const option = allOptions.find((o) => o.id === filterValueAsArray[i]);
    if (!option) return false;
    if (isNumberInRange(variantValue, option.range)) {
      return true;
    }
  }
  if (!isInRange) {
    return false;
  }
};

export const updateUrlQuery = (filters, searchQuery, sort) => {
  const paramMap = {};
  forEach(filters, (v, k) => {
    if (k === 'depreciation') {
      paramMap[k] = v;
      return;
    }
    if (v.size > 0) {
      paramMap[k] = Array.from(v);
    }
  });
  if (searchQuery) {
    paramMap.query = searchQuery;
  }
  if (sort) {
    paramMap.sort = sort;
  }
  const params = new URLSearchParams(paramMap);
  window.history.replaceState(null, null, `?${params.toString()}`);
};

export function isElementInViewport(el) {
  const rect = el.getBoundingClientRect();

  return (
    rect.bottom > 0 &&
    rect.right > 0 &&
    rect.left <
      (window.innerWidth ||
        document.documentElement.clientWidth) /* or $(window).width() */ &&
    rect.top <
      (window.innerHeight ||
        document.documentElement.clientHeight) /* or $(window).height() */
  );
}
